import React, { useRef, useEffect, useState } from 'react'

import { Text, TextName } from 'sdk/text/Text'

import { TestimonialsCard } from '../testimonials-card/TestimonialsCard'

import styles from './style.module.scss'

interface Testimonial {
  text: string;
  name: string;
  title: string;
  avatarSrc: string;
  avatarLink?: string;
}

interface TestimonialsSectionProps {
  title: string;
  testimonials: Testimonial[];
}

export const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ title, testimonials }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const animationRef = useRef<number>()

  useEffect(() => {
    const scrollContainer = scrollRef.current
    const scrollContent = contentRef.current
    if (!scrollContainer || !scrollContent) {
      return
    }

    const scrollWidth = scrollContent.scrollWidth / 2

    const animate = (): void => {
      if (!isDragging && !isPaused && scrollContainer) {
        scrollContainer.scrollLeft += 1

        if (scrollContainer.scrollLeft >= scrollWidth) {
          scrollContainer.scrollLeft = 0
        }
      }
      animationRef.current = requestAnimationFrame(animate)
    }

    animationRef.current = requestAnimationFrame(animate)

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isDragging, isPaused])

  const handleMouseDown = (e: React.MouseEvent): void => {
    setIsDragging(true)
    setStartX(e.pageX - (scrollRef.current?.offsetLeft || 0))
    setScrollLeft(scrollRef.current?.scrollLeft || 0)
  }

  const handleMouseMove = (e: React.MouseEvent): void => {
    if (!isDragging || !scrollRef.current) {
      return
    }
    e.preventDefault()
    const x = e.pageX - (scrollRef.current.offsetLeft || 0)
    const walk = (x - startX) * 2
    scrollRef.current.scrollLeft = scrollLeft - walk
  }

  const handleMouseUp = (): void => {
    setIsDragging(false)
  }

  const handleMouseLeave = (): void => {
    setIsDragging(false)
  }

  const handleCardInteraction = (isExpanded: boolean): void => {
    setIsPaused(isExpanded)
  }

  return (
    <div className={styles.section}>
      <Text type={TextName.H2} className={styles.title}>{title}</Text>
      <div
        ref={scrollRef}
        className={styles.scrollContainer}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div ref={contentRef} className={styles.scrollContent}>
          {testimonials.map((testimonial, index) => (
            <div key={index} className={styles.cardWrapper}>
              <TestimonialsCard {...testimonial} onExpandChange={handleCardInteraction} />
            </div>
          ))}
          {testimonials.map((testimonial, index) => (
            <div key={`duplicate-${index}`} className={styles.cardWrapper}>
              <TestimonialsCard {...testimonial} onExpandChange={handleCardInteraction} />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
